import { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, Loader2, Users, Thermometer, ArrowLeft, DollarSign, ChevronDown, AlertCircle } from 'lucide-react';
import { searchLeads } from '../../services/salesCoachContextService';
import type { PipelineStageConfig } from '../../hooks/usePipelineStagesConfig';

interface CreateDealModalProps {
  stages: PipelineStageConfig[];
  existingLeadIds: string[];
  salesPersons: string[];
  currentUserName: string;
  onCreateDeal: (params: {
    lead_id: string;
    pipeline_stage: string;
    sales_person: string;
    deal_value?: number | null;
    notes?: string | null;
  }) => Promise<string | undefined>;
  onClose: () => void;
}

export function CreateDealModal({
  stages,
  existingLeadIds,
  salesPersons,
  currentUserName,
  onCreateDeal,
  onClose,
}: CreateDealModalProps) {
  const [step, setStep] = useState<'search' | 'configure'>('search');
  const [selectedLead, setSelectedLead] = useState<any>(null);

  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [selectedStage, setSelectedStage] = useState(stages[0]?.name || '');
  const [salesPerson, setSalesPerson] = useState(currentUserName);
  const [dealValue, setDealValue] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPersonDropdown, setShowPersonDropdown] = useState(false);
  const personRef = useRef<HTMLDivElement>(null);
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  const existingSet = useRef(new Set(existingLeadIds));
  useEffect(() => { existingSet.current = new Set(existingLeadIds); }, [existingLeadIds]);

  const doSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    setLoading(true);
    setHasSearched(true);
    const data = await searchLeads(query);
    setResults(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(search), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, doSearch]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (personRef.current && !personRef.current.contains(e.target as Node)) {
        setShowPersonDropdown(false);
      }
      if (stageRef.current && !stageRef.current.contains(e.target as Node)) {
        setShowStageDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelectLead = (lead: any) => {
    setSelectedLead(lead);
    setStep('configure');
    setSubmitError(null);
  };

  const handleBack = () => {
    setStep('search');
    setSelectedLead(null);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!selectedLead || !selectedStage || !salesPerson.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onCreateDeal({
        lead_id: selectedLead.lead_id,
        pipeline_stage: selectedStage,
        sales_person: salesPerson.trim(),
        deal_value: dealValue ? parseFloat(dealValue) : null,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to create deal');
    } finally {
      setSubmitting(false);
    }
  };

  const getTempBadge = (temp: string) => {
    const t = temp?.toLowerCase() || '';
    if (t.includes('hot')) return { bg: 'bg-red-50 text-red-600 border-red-200', label: 'Hot' };
    if (t.includes('warm')) return { bg: 'bg-amber-50 text-amber-600 border-amber-200', label: 'Warm' };
    if (t.includes('cold')) return { bg: 'bg-blue-50 text-blue-600 border-blue-200', label: 'Cold' };
    return null;
  };

  const getStatusBadge = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('active') || s.includes('engaged')) return 'bg-emerald-50 text-emerald-600 border-emerald-200';
    if (s.includes('new')) return 'bg-blue-50 text-blue-600 border-blue-200';
    if (s.includes('dead') || s.includes('lost') || s.includes('not_interested')) return 'bg-gray-100 text-gray-500 border-gray-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const formatStatus = (status: string) =>
    status?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Unknown';

  const allPersonOptions = Array.from(new Set([currentUserName, ...salesPersons].filter(Boolean)));

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-gray-200" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-3">
            {step === 'configure' && (
              <button
                onClick={handleBack}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={18} className="text-gray-500" />
              </button>
            )}
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">
                {step === 'search' ? 'Search for a Lead' : 'Create a Deal'}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {step === 'search'
                  ? 'Find any lead by company name or contact'
                  : 'Configure deal details for this lead'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {step === 'search' ? (
          <>
            <div className="px-6 pt-4 pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Type company name or contact name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#1875FF] focus:ring-2 focus:ring-[#1875FF]/20 transition-all text-sm"
                  autoFocus
                />
              </div>
            </div>

            <div className="px-4 pb-4 max-h-80 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-[#1875FF] animate-spin" />
                </div>
              ) : !hasSearched ? (
                <div className="text-center py-12">
                  <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm font-medium">Start typing to search leads</p>
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm font-medium">No leads found for "{search}"</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {results.map((lead) => {
                    const temp = getTempBadge(lead.ovrall_lead_temp);
                    const alreadyInPipeline = existingSet.current.has(lead.lead_id);
                    return (
                      <button
                        key={lead.lead_id}
                        onClick={() => !alreadyInPipeline && handleSelectLead(lead)}
                        disabled={alreadyInPipeline}
                        className={`w-full flex items-center gap-4 p-3.5 rounded-xl border text-left group transition-all ${
                          alreadyInPipeline
                            ? 'border-gray-100 bg-gray-50/50 opacity-60 cursor-not-allowed'
                            : 'border-gray-100 hover:border-[#1875FF]/40 hover:bg-[#1875FF]/5'
                        }`}
                      >
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center">
                          <Users size={20} className="text-[#1875FF]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 text-sm truncate group-hover:text-[#1875FF] transition-colors">
                            {lead.company_name || 'Unknown Company'}
                          </div>
                          <div className="text-xs text-gray-500 truncate mt-0.5">
                            {lead.dm_full_name || 'No contact'}{lead.dm_title ? ` - ${lead.dm_title}` : ''}
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {alreadyInPipeline && (
                              <span className="text-xs px-2 py-0.5 rounded-full border font-medium bg-gray-100 text-gray-500 border-gray-200">
                                Already in Pipeline
                              </span>
                            )}
                            {lead.ovrall_lead_status && !alreadyInPipeline && (
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getStatusBadge(lead.ovrall_lead_status)}`}>
                                {formatStatus(lead.ovrall_lead_status)}
                              </span>
                            )}
                            {temp && !alreadyInPipeline && (
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium inline-flex items-center gap-1 ${temp.bg}`}>
                                <Thermometer size={10} />
                                {temp.label}
                              </span>
                            )}
                            {lead.industry_1 && !alreadyInPipeline && (
                              <span className="text-xs text-gray-400">{lead.industry_1}</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="px-6 py-5 space-y-5">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 border border-[#1875FF]/10">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/15 to-[#05E0D1]/15 flex items-center justify-center">
                <Users size={20} className="text-[#1875FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900 text-sm truncate">
                  {selectedLead?.company_name || 'Unknown Company'}
                </div>
                <div className="text-xs text-gray-500 truncate mt-0.5">
                  {selectedLead?.dm_full_name || 'No contact'}{selectedLead?.dm_title ? ` - ${selectedLead.dm_title}` : ''}
                </div>
                {selectedLead?.industry_1 && (
                  <div className="text-xs text-gray-400 mt-1">{selectedLead.industry_1}</div>
                )}
              </div>
            </div>

            <div ref={stageRef} className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Pipeline Stage</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowStageDropdown(!showStageDropdown)}
                  className="w-full px-3 py-2.5 pr-9 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:border-[#1875FF] focus:ring-2 focus:ring-[#1875FF]/20 transition-all text-left"
                >
                  {selectedStage ? `${selectedStage} (${stages.find(s => s.name === selectedStage)?.closing_probability ?? 0}%)` : 'Select a stage'}
                </button>
                <ChevronDown
                  size={16}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none transition-transform ${showStageDropdown ? 'rotate-180' : ''}`}
                />
              </div>
              {showStageDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {stages.map(s => (
                    <button
                      key={s.id}
                      onClick={() => {
                        setSelectedStage(s.name);
                        setShowStageDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors first:rounded-t-xl last:rounded-b-xl ${
                        selectedStage === s.name
                          ? 'bg-[#1875FF]/10 text-[#1875FF] font-medium'
                          : 'text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF]'
                      }`}
                    >
                      {s.name} ({s.closing_probability}%)
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div ref={personRef} className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Sales Person</label>
              <div className="relative">
                <input
                  type="text"
                  value={salesPerson}
                  onChange={(e) => {
                    setSalesPerson(e.target.value);
                    setShowPersonDropdown(true);
                  }}
                  onFocus={() => setShowPersonDropdown(true)}
                  className="w-full px-3 py-2.5 pr-9 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:border-[#1875FF] focus:ring-2 focus:ring-[#1875FF]/20 transition-all"
                  placeholder="Enter sales person name"
                />
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
              {showPersonDropdown && allPersonOptions.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                  {allPersonOptions
                    .filter(name => name.toLowerCase().includes(salesPerson.toLowerCase()))
                    .map(name => (
                      <button
                        key={name}
                        onClick={() => {
                          setSalesPerson(name);
                          setShowPersonDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors first:rounded-t-xl last:rounded-b-xl"
                      >
                        {name}
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Deal Value (optional)</label>
              <div className="relative">
                <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={dealValue}
                  onChange={(e) => setDealValue(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:border-[#1875FF] focus:ring-2 focus:ring-[#1875FF]/20 transition-all"
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:border-[#1875FF] focus:ring-2 focus:ring-[#1875FF]/20 transition-all resize-none"
                rows={2}
                placeholder="Add any notes about this deal..."
              />
            </div>

            {submitError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                <span className="text-sm text-red-600">{submitError}</span>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={submitting || !selectedStage || !salesPerson.trim()}
              className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] hover:shadow-lg hover:shadow-[#1875FF]/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Deal'
              )}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
